<template>
  <div class="product-display-carousel">
    <h2 v-if="productDisplayCarouselData.title" class="title">
      {{ truncateText(productDisplayCarouselData.title, 57) }}
    </h2>
    <p v-if="productDisplayCarouselData.subtitle" class="sub-title">
      {{ truncateText(productDisplayCarouselData.subtitle, 77) }}
    </p>

    <div class="carousel-area">
      <button
        v-if="productDisplayCarouselData.products.length > itemsPerSlide"
        class="arrow-icon left"
        :class="{ disabled: currentIndex == 0 }"
        aria-label="Previous Slide"
        @click="prevSlide"
      >
        <svg
          width="8"
          height="14"
          viewBox="0 0 8 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M7.33026 0L0.181641 7L7.33026 14L7.8181 13.5262L1.15626 7L7.8181 0.473846L7.33026 0Z"
            fill="#242424"
          />
        </svg>
      </button>
      <div class="overflow-hidden">
        <div class="product-cards" :style="carouselStyle">
          <div
            v-for="(product, index) in productDisplayCarouselData.products"
            :key="index"
            class="product-card"
            :class="{ visible: index >= currentIndex && index < currentIndex + itemsPerSlide }"
            tabindex="0"
            role="group"
            @click="handleProductClick(product)"
            @keypress.enter="handleProductClick(product)"
            @mouseover="!isMobile && swapImage(index, true)"
            @mouseleave="!isMobile && swapImage(index, false)"
          >
            <div class="image-wrapper">
              <picture v-if="getProductImage(index)">
                <source media="(max-width: 767px)" :data-srcset="getProductImage(index)" />
                <source
                  media="(min-width: 768px) and (max-width: 1199px)"
                  :data-srcset="getProductImage(index)"
                />
                <source media="(min-width: 1200px)" :data-srcset="getProductImage(index)" />
                <img
                  :alt="product.productFirstImage ? product.productFirstImage.Alt : 'Product Image'"
                  class="product-image lazyload"
                  loading="lazy"
                />
              </picture>
            </div>

            <div class="product-info">
              <h3 class="product-name">{{ truncateText(product.title, 104) }}</h3>
              <p class="product-summary">{{ truncateText(product.summary, 59) }}</p>
              <p class="product-description">{{ truncateText(product.productdescription, 99) }}</p>
            </div>
            <button class="product-cta" :aria-label="`${product.title}`">
              {{ truncateText(productDisplayCarouselData.cta_label, 26) }}
            </button>
          </div>
        </div>
      </div>
      <button
        v-if="productDisplayCarouselData.products.length > itemsPerSlide"
        :class="{
          disabled: currentIndex + itemsPerSlide == productDisplayCarouselData.products.length,
        }"
        class="arrow-icon right"
        aria-label="Next Slide"
        @click="nextSlide"
      >
        <svg
          width="8"
          height="14"
          viewBox="0 0 8 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M0.669499 14L7.81812 7L0.669501 -1.57863e-06L0.181654 0.473845L6.8435 7L0.181653 13.5262L0.669499 14Z"
            fill="#242424"
          />
        </svg>
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'product-card-carousel',
  props: {
    productDisplayCarouselData: Object,
  },
  data() {
    return {
      currentIndex: 0,
      hoveredIndex: null,
      triggeredImpression: [],
      isImpressionDone: false,
    };
  },
  computed: {
    itemsPerSlide() {
      return window.innerWidth <= 768 ? 1 : 4;
    },
    visibleProducts() {
      return this.productDisplayCarouselData.products.slice(
        this.currentIndex,
        this.currentIndex + this.itemsPerSlide,
      );
    },
    isMobile() {
      return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    },
    carouselStyle() {
      return {
        transform: `translateX(-${this.currentIndex * (243 + 24)}px)`,
        transition: `transform 300ms ease-in-out`,
      };
    },
  },
  watch: {
    visibleProducts: {
      handler(newValue, oldValue) {
        if (JSON.stringify(newValue) !== JSON.stringify(oldValue)) {
          this.isImpressionDone = false;
          this.productImpression();
        }
      },
    },
  },

  mounted() {
    this.handleScroll();
    window.addEventListener('scroll', this.handleScroll);
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll);
  },
  methods: {
    isInViewport(element) {
      if (!element || typeof element.getBoundingClientRect !== 'function') {
        return false;
      }
      const rect = element.getBoundingClientRect();
      return (
        rect.bottom > 0 &&
        rect.right > 0 &&
        rect.top < (window.innerHeight || document.documentElement.clientHeight) &&
        rect.left < (window.innerWidth || document.documentElement.clientWidth)
      );
    },

    handleScroll() {
      const element = document.getElementsByClassName('product-display-carousel');
      const isViewport = this.isInViewport(element ? element[0] : null);
      if (isViewport && !this.isImpressionDone) {
        this.productImpression();
      }
    },
    truncateText(text, maxLength) {
      return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    },
    swapImage(index, hover) {
      this.hoveredIndex = hover ? index : null;
    },
    getProductImage(index) {
      const product = this.productDisplayCarouselData.products[index];

      if (!product) return '';

      if (
        this.hoveredIndex === index &&
        product.productSecondImage &&
        product.productSecondImage.Src
      ) {
        return product.productSecondImage.Src;
      }

      return product.productFirstImage && product.productFirstImage.Src
        ? product.productFirstImage.Src
        : '';
    },

    nextSlide() {
      if (
        this.currentIndex + this.itemsPerSlide <
        this.productDisplayCarouselData.products.length
      ) {
        this.currentIndex++;
      }
    },
    prevSlide() {
      if (this.currentIndex > 0) {
        this.currentIndex--;
      }
    },
    handleProductClick(product) {
      this.productClicked(product);
    },
    redirectToProduct(url) {
      if (url && url !== '#') {
        window.open(url, '_blank');
      }
    },
    productImpression() {
      // NOSONAR
      if (this.visibleProducts && this.visibleProducts.length) {
        const impressions = [];
        this.visibleProducts.forEach((product) => {
          const taggingData = JSON.parse(product.productcarousel_impression_tagging_attribute);

          if (taggingData && taggingData.ecommerce && taggingData.ecommerce.impressions) {
            const impressionData = taggingData.ecommerce.impressions[0];
            if (!this.triggeredImpression.includes(impressionData.id)) {
              this.triggeredImpression.push(impressionData.id);
            } else {
              return;
            }
            impressions.push({
              name: impressionData.name,
              id: impressionData.id,
              price: impressionData.price,
              brand: impressionData.brand,
              category: impressionData.category,
              variant: decodeURIComponent(impressionData.variant),
              list: impressionData.list,
              position: impressionData.position,
              dimension35: decodeURIComponent(impressionData.dimension35),
              dimension36: impressionData.dimension36,
              dimension38: impressionData.dimension38,
              dimension48: impressionData.dimension48,
              dimension49: impressionData.dimension49,
              dimension59: impressionData.dimension59,
              dimension91: impressionData.dimension91,
            });
          }
        });
        try {
          if (impressions && impressions.length && !this.isImpressionDone) {
            const taggingData = JSON.parse(
              this.productDisplayCarouselData.products[0]
                .productcarousel_impression_tagging_attribute,
            );

            window.dataLayer.push({
              event: taggingData.event,
              event_name: taggingData.event_name,
              eventCategory: taggingData.eventCategory,
              eventAction: taggingData.eventAction,
              eventLabel: taggingData.eventLabel,
              ecommerce: {
                currencyCode:
                  taggingData && taggingData.ecommerce && taggingData.ecommerce.currencyCode
                    ? taggingData.ecommerce.currencyCode
                    : 'none',
                impressions: impressions,
              },
            });
            this.isImpressionDone = true;
          }
        } catch {
          console.warn('datalayer is not defined');
        }
      }
    },

    productClicked(product) {
      try {
        const taggingData = JSON.parse(product.productcarousel_click_tagging_attribute);

        if (taggingData && taggingData.ecommerce && taggingData.ecommerce.impressions) {
          const impressionData = taggingData.ecommerce.impressions[0];

          const productClickTag = {
            event: taggingData.event,
            event_name: taggingData.event_name,
            eventCategory: taggingData.eventCategory,
            eventAction: taggingData.eventAction,
            eventLabel: taggingData.eventLabel,
            product_info: taggingData.product_info,
            ecommerce: {
              click: {
                actionField: [],
                list: impressionData.list,
                products: {
                  name: impressionData.name,
                  id: impressionData.id,
                  brand: impressionData.brand,
                  category: impressionData.category,
                  position: impressionData.position,
                  variant: decodeURIComponent(impressionData.variant),
                  price: impressionData.price,
                  dimension35: decodeURIComponent(impressionData.dimension35),
                  dimension36: impressionData.dimension36,
                  dimension38: impressionData.dimension38,
                  dimension48: impressionData.dimension48,
                  dimension49: impressionData.dimension49,
                  dimension59: impressionData.dimension59,
                  dimension91: impressionData.dimension91,
                },
              },
            },
          };

          window.dataLayer.push(productClickTag);
        }
      } catch (error) {
        console.error('Tagging error:', error); // Log error but continue execution
      }

      // Ensures redirection happens even if tagging fails
      this.redirectToProduct(product.url);
    },
  },
};
</script>
